import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import {
  useNavigation,
  useRoute,
  RouteProp,
} from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import { BrandLogo } from '../../components/BrandLogo';
import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { fpColors } from '../../styles/theme';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { selectIsAuthenticated } from '../auth/authSlice';
import {
  acceptGroupInvite,
  selectGroupsLoading,
  selectGroupsError,
  clearGroupsError,
} from './groupsSlice';
import {
  setPendingInviteToken,
  clearPendingInviteToken,
} from './pendingInvite';
import type { GroupDetail } from '../../api/types';

/**
 * AcceptInviteScreen — Sprint 1 (Chunk 8).
 *
 * Lands at `https://app.remembr.xyz/invite/{token}` (deep link
 * `AcceptInvite/:token`). Reachable when unauthenticated.
 *
 * Five render states (BDD on issue #30):
 *   A — unauthenticated, fresh visit: title + Google CTA + email CTA
 *   B — accepting in flight: spinner
 *   C — success: "You've joined {group_name}" + [Open crew]
 *   D — expired: amber "invite expired" banner
 *   E — invalid / not found: error banner
 *   F — already a member: "You're already in {group_name}"
 *
 * Token is stashed to localStorage on mount so a refresh / nav-away
 * doesn't lose it. App-boot resume hook (in AppNavigator) clears the
 * token after successful accept.
 */

type AcceptInviteRouteParams = {
  AcceptInvite: { token: string };
};

type ErrorKind = 'expired' | 'not_found' | 'already_member' | 'unknown';

function classifyError(detail: string | undefined): ErrorKind {
  const d = (detail || '').toLowerCase();
  if (d.includes('expir')) return 'expired';
  // PR31 — server returns "Invite not found or already redeemed" as
  // a single 404 detail for BOTH the not-found and the already-redeemed
  // case. Map "already redeemed" → already_member so the screen
  // routes the user to the group instead of "expired link".
  if (d.includes('already')) return 'already_member';
  if (d.includes('member')) return 'already_member';
  if (d.includes('redeemed')) return 'already_member';
  if (d.includes('not found') || d.includes('unknown') || d.includes('invalid'))
    return 'not_found';
  return 'unknown';
}

export const AcceptInviteScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<AcceptInviteRouteParams, 'AcceptInvite'>>();
  const dispatch = useAppDispatch();

  const token = route.params?.token ?? '';
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectGroupsLoading);
  const error = useAppSelector(selectGroupsError);

  const [acceptedGroup, setAcceptedGroup] = useState<GroupDetail | null>(null);
  const [hasTried, setHasTried] = useState(false);

  // Stash token on mount so a refresh / accidental nav-away preserves
  // the invite context. Cleared on success or unrecoverable error.
  useEffect(() => {
    if (token) setPendingInviteToken(token);
    return () => {
      // Only clear on unmount when we actually accepted; preserve on
      // partial flow (email-signin redirect path) so the boot hook can
      // resume later.
    };
  }, [token]);

  // Auto-accept whenever we have an authenticated session + a token.
  useEffect(() => {
    if (!token) return;
    if (!isAuthenticated) return;
    if (hasTried) return;
    if (acceptedGroup) return;

    setHasTried(true);
    dispatch(clearGroupsError());
    dispatch(acceptGroupInvite(token))
      .unwrap()
      .then((res) => {
        // Defensive: BE may return either {group: GroupDetail, membership}
        // or GroupDetail directly (rev-3 batch). See groupsSlice.acceptGroupInvite
        // .fulfilled reducer for the same coalesce.
        setAcceptedGroup(
          (res as any)?.group ?? ((res as unknown) as GroupDetail),
        );
        clearPendingInviteToken();
      })
      .catch((err: any) => {
        // ALREADY_REDEEMED soft-success: token was already consumed
        // (user accepted earlier and is now revisiting the link). Per
        // PR32 + the invite_consumed mapping, groupsSlice no longer
        // writes state.error in this case — so the render below would
        // otherwise stay on "Joining the group..." forever.
        //
        // Two recovery paths in priority order:
        //   1. context.group_id is set → jump straight to GroupDetail
        //   2. fall back to the Groups list so the user can pick the
        //      one they joined
        if (err && typeof err === 'object' && err.code === 'ALREADY_REDEEMED') {
          clearPendingInviteToken();
          const groupId = err?.context?.group_id;
          if (typeof groupId === 'number') {
            navigation.replace('GroupDetail', { groupId: String(groupId) });
          } else {
            navigation.replace('CrewHub');
          }
          return;
        }
        // Other errors surface via the slice's `error` field; classifyError()
        // below decides which UI state renders. Token stays in localStorage
        // for non-recoverable errors (expired/not_found) — they're cleared
        // when the user dismisses; for already_member we clear immediately
        // since they should go to the group.
      });
  }, [token, isAuthenticated, hasTried, acceptedGroup, dispatch, navigation]);

  // ── Handlers ────────────────────────────────────────────────────

  const goEmailSignIn = useCallback(() => {
    // Persist the token + redirect to Login. After login, the AppNavigator
    // boot-resume hook detects auth+pending-token and re-routes here.
    if (token) setPendingInviteToken(token);
    navigation.navigate('Login', {
      returnTo: 'AcceptInvite',
      returnParams: { token },
    });
  }, [navigation, token]);

  const goCrew = useCallback(() => {
    if (!acceptedGroup) return;
    navigation.reset({
      index: 1,
      routes: [
        { name: 'MainTabs' as any },
        {
          name: 'GroupDetail' as any,
          params: { groupId: String(acceptedGroup.id) },
        },
      ],
    });
  }, [acceptedGroup, navigation]);

  const goCrewHub = useCallback(() => {
    clearPendingInviteToken();
    navigation.reset({
      index: 0,
      routes: [{ name: 'MainTabs' as any, params: { screen: 'Community' } }],
    });
  }, [navigation]);

  const goHome = useCallback(() => {
    clearPendingInviteToken();
    navigation.reset({ index: 0, routes: [{ name: 'MainTabs' as any }] });
  }, [navigation]);

  // ── Render ──────────────────────────────────────────────────────

  if (!token) {
    return (
      <View style={styles.page}>
        <Header />
        <View style={styles.body}>
          <ErrorState
            tone="error"
            icon="error-outline"
            title="Invite link missing"
            body="That URL didn't include an invite token."
            ctaLabel="Back to home"
            onPress={goHome}
          />
        </View>
      </View>
    );
  }

  // STATE C — success
  if (acceptedGroup) {
    return (
      <View style={styles.page}>
        <Header />
        <View style={styles.body}>
          <View style={[styles.iconTile, styles.iconTileSuccess]}>
            <MaterialIcons name="check" size={32} color={fpColors.onPrimary} />
          </View>
          <Text style={styles.h1} accessibilityRole="header">
            You've joined {acceptedGroup.name}
          </Text>
          <Text style={styles.subtitle}>
            {acceptedGroup.members.filter((m) => m.status === 'ACTIVE').length}{' '}
            members
          </Text>
          <View style={styles.ctaStack}>
            <Pressable
              onPress={goCrew}
              style={({ pressed }) => [
                styles.primaryCta,
                pressed && { opacity: 0.88 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Open ${acceptedGroup.name}`}
            >
              <Text style={styles.primaryCtaText}>Open circle</Text>
              <MaterialIcons
                name="arrow-forward"
                size={16}
                color={fpColors.onPrimary}
              />
            </Pressable>
            <Pressable
              onPress={goCrewHub}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="View all your circles"
            >
              <Text style={styles.secondaryLink}>View all your circles</Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  }

  // STATE B — accepting
  if (isAuthenticated && (loading.accepting || hasTried) && !error) {
    return (
      <View style={styles.page}>
        <Header />
        <View style={styles.body}>
          <ActivityIndicator size="large" color={fpColors.primary} />
          <Text style={styles.subtitle}>Joining the circle…</Text>
        </View>
      </View>
    );
  }

  // STATE D / E / F — error after authenticated attempt
  if (isAuthenticated && error && hasTried) {
    const kind = classifyError(error);
    if (kind === 'already_member') {
      return (
        <View style={styles.page}>
          <Header />
          <View style={styles.body}>
            <View style={[styles.iconTile, styles.iconTileSuccess]}>
              <MaterialIcons name="check" size={32} color={fpColors.onPrimary} />
            </View>
            <Text style={styles.h1}>You're already in this circle</Text>
            <Pressable
              onPress={goCrewHub}
              style={({ pressed }) => [
                styles.primaryCta,
                pressed && { opacity: 0.88 },
              ]}
              accessibilityRole="button"
            >
              <Text style={styles.primaryCtaText}>Open your circles</Text>
              <MaterialIcons
                name="arrow-forward"
                size={16}
                color={fpColors.onPrimary}
              />
            </Pressable>
          </View>
        </View>
      );
    }
    if (kind === 'expired') {
      return (
        <View style={styles.page}>
          <Header />
          <View style={styles.body}>
            <ErrorState
              tone="warning"
              icon="schedule"
              title="Invite expired"
              body="This invitation expired. Ask the circle owner to send a new one."
              ctaLabel="Back to your circles"
              onPress={goCrewHub}
            />
          </View>
        </View>
      );
    }
    return (
      <View style={styles.page}>
        <Header />
        <View style={styles.body}>
          <ErrorState
            tone="error"
            icon="link-off"
            title="Invite not found"
            body={
              kind === 'not_found'
                ? "That link doesn't match a known invite. The token may have been mistyped or revoked."
                : (error || 'Could not accept the invite. Try again later.')
            }
            ctaLabel="Back to home"
            onPress={goHome}
          />
        </View>
      </View>
    );
  }

  // STATE A — unauthenticated, fresh visit
  return (
    <View style={styles.page}>
      <Header />
      <View style={styles.body}>
        <View style={styles.iconTile}>
          <MaterialIcons name="group" size={32} color={fpColors.primary} />
        </View>
        <Text style={styles.h1} accessibilityRole="header">
          You've been invited
        </Text>
        <Text style={styles.subtitle}>
          to join a Founsi circle. Sign in or sign up to accept.
        </Text>

        <View style={styles.ctaStack}>
          <GoogleSignInButton label="Continue with Google" />
          <Pressable
            onPress={goEmailSignIn}
            hitSlop={6}
            style={styles.emailLink}
            accessibilityRole="button"
            accessibilityLabel="Continue with email instead"
          >
            <Text style={styles.emailLinkText}>Continue with email</Text>
            <MaterialIcons
              name="arrow-forward"
              size={16}
              color={fpColors.primary}
            />
          </Pressable>
        </View>
      </View>
    </View>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Sub-components
 * ──────────────────────────────────────────────────────────────────── */

const Header: React.FC = () => (
  <View style={styles.topBar}>
    <BrandLogo variant="horizontal" height={22} tone="color" />
  </View>
);

const ErrorState: React.FC<{
  tone: 'warning' | 'error';
  icon: any;
  title: string;
  body: string;
  ctaLabel: string;
  onPress: () => void;
}> = ({ tone, icon, title, body, ctaLabel, onPress }) => (
  <>
    <View
      style={[
        styles.iconTile,
        tone === 'warning' ? styles.iconTileWarning : styles.iconTileError,
      ]}
    >
      <MaterialIcons
        name={icon}
        size={32}
        color={tone === 'warning' ? fpColors.tertiary : fpColors.error}
      />
    </View>
    <Text style={styles.h1} accessibilityRole="header">
      {title}
    </Text>
    <Text style={styles.subtitle}>{body}</Text>
    <View style={styles.ctaStack}>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [
          styles.primaryCta,
          pressed && { opacity: 0.88 },
        ]}
        accessibilityRole="button"
        accessibilityLabel={ctaLabel}
      >
        <Text style={styles.primaryCtaText}>{ctaLabel}</Text>
      </Pressable>
    </View>
  </>
);

/* ────────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  topBar: {
    paddingTop: Platform.OS === 'ios' ? 56 : 18,
    paddingBottom: 16,
    paddingHorizontal: 16,
    alignItems: 'flex-start',
  },
  body: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 48,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
  },

  iconTile: {
    width: 72,
    height: 72,
    borderRadius: 999,
    backgroundColor: `${fpColors.primary}1f`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  iconTileSuccess: {
    backgroundColor: fpColors.primary,
  },
  iconTileWarning: {
    backgroundColor: `${fpColors.tertiaryContainer}66`,
  },
  iconTileError: {
    backgroundColor: `${fpColors.errorContainer}66`,
  },

  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    letterSpacing: -0.5,
    color: fpColors.onSurface,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    paddingHorizontal: 16,
    marginTop: -4,
  },

  ctaStack: {
    width: '100%',
    maxWidth: 320,
    gap: 12,
    marginTop: 16,
  },
  primaryCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: fpColors.primary,
  },
  primaryCtaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
  emailLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
  },
  emailLinkText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.primary,
  },
  secondaryLink: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.primary,
    textAlign: 'center',
    paddingVertical: 8,
  },
});
